import type { EntryWidgetProps } from './types';

/*
 * Percent entry — correct / total inputs. Derives valueNumeric from the
 * pair; valueJson stores {correct, total} raw. Unlike trial grids, the
 * total isn't fixed — percent targets cover variable-opportunity tasks
 * (task analyses with variable probes, naturalistic teaching, etc.).
 */
export function PercentEntry({ value, onChange, disabled }: EntryWidgetProps) {
    const current = (() => {
        const v = value.valueJson && 'correct' in value.valueJson
            ? (value.valueJson as { correct: number; total: number })
            : null;
        return { correct: v?.correct ?? 0, total: v?.total ?? 0 };
    })();

    // When the numerator is edited, let it lead: if correct would exceed
    // total, bump total up to match rather than clamping correct down.
    // When the denominator is edited, keep the clamp so correct stays <= total.
    const commit = (correct: number, total: number, source: 'correct' | 'total') => {
        const flooredCorrect = Math.max(0, Math.floor(correct));
        const flooredTotal = Math.max(0, Math.floor(total));
        const safeTotal = source === 'correct' && flooredCorrect > flooredTotal
            ? flooredCorrect
            : flooredTotal;
        const safeCorrect = Math.min(safeTotal, flooredCorrect);
        const pct = safeTotal > 0 ? Math.round((safeCorrect / safeTotal) * 100) : 0;
        onChange({
            ...value,
            valueNumeric: pct,
            valueJson: { correct: safeCorrect, total: safeTotal },
            dirty: true,
        });
    };

    return (
        <div className="flex items-center gap-2">
            <input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                placeholder="correct"
                value={current.correct || ''}
                onChange={(e) => commit(Number(e.target.value || 0), current.total, 'correct')}
                disabled={disabled}
                className="w-20 px-2 py-1 text-center text-sm bg-background border border-border rounded-md focus:border-accent focus:outline-none disabled:opacity-50"
            />
            <span className="text-text-subtle">/</span>
            <input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                placeholder="total"
                value={current.total || ''}
                onChange={(e) => commit(current.correct, Number(e.target.value || 0), 'total')}
                disabled={disabled}
                className="w-20 px-2 py-1 text-center text-sm bg-background border border-border rounded-md focus:border-accent focus:outline-none disabled:opacity-50"
            />
            <span className="text-sm font-semibold text-text ml-1">
                = {value.valueNumeric ?? 0}%
            </span>
        </div>
    );
}
